// Analytics — GA4 + PostHog
// Both are optional: only initialise when env vars are present

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com'

// ── GA4 ───────────────────────────────────────────────────────────────────────

export function initGA4() {
  if (!GA4_ID || typeof window === 'undefined') return
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`
  document.head.appendChild(script)
  window.dataLayer = window.dataLayer ?? []
  window.gtag = function (...args: unknown[]) { window.dataLayer!.push(args) }
  window.gtag('js', new Date())
  window.gtag('config', GA4_ID, { send_page_view: false })
}

function ga4Event(name: string, params?: Record<string, unknown>) {
  if (!GA4_ID || !window.gtag) return
  window.gtag('event', name, params)
}

function ga4PageView(path: string) {
  if (!GA4_ID || !window.gtag) return
  window.gtag('event', 'page_view', { page_path: path, send_to: GA4_ID })
}

// ── PostHog ───────────────────────────────────────────────────────────────────

let posthog: { capture: (e: string, p?: Record<string, unknown>) => void; identify: (id: string, props?: Record<string, unknown>) => void } | null = null

export async function initPostHog() {
  if (!POSTHOG_KEY || typeof window === 'undefined') return
  const { default: ph } = await import('posthog-js')
  ph.init(POSTHOG_KEY, { api_host: POSTHOG_HOST, capture_pageview: false, persistence: 'localStorage' })
  posthog = ph
}

// ── Unified API ───────────────────────────────────────────────────────────────

// ── AI-assistant referral classification (PRI-492) ─────────────────────────

const AI_REFERRER_HOSTS: Array<[RegExp, string]> = [
  [/(^|\.)chatgpt\.com$/, 'chatgpt'],
  [/^chat\.openai\.com$/, 'chatgpt'],
  [/(^|\.)perplexity\.ai$/, 'perplexity'],
  [/(^|\.)claude\.ai$/, 'claude'],
  [/^gemini\.google\.com$/, 'gemini'],
  [/^copilot\.microsoft\.com$/, 'copilot'],
]

/**
 * Classify a visit as coming from an AI assistant. Checks the document
 * referrer hostname against known assistant hosts, plus the
 * `utm_source=chatgpt.com` param ChatGPT appends to cited links.
 * Returns the assistant name or null. Exported for tests.
 */
export function classifyAiReferral(referrer: string, search: string): string | null {
  try {
    const utm = new URLSearchParams(search).get('utm_source')
    if (utm) {
      const hit = AI_REFERRER_HOSTS.find(([re]) => re.test(utm))
      if (hit) return hit[1]
    }
  } catch { /* malformed search string — ignore */ }
  if (referrer) {
    try {
      const host = new URL(referrer).hostname
      const hit = AI_REFERRER_HOSTS.find(([re]) => re.test(host))
      if (hit) return hit[1]
    } catch { /* malformed referrer — ignore */ }
  }
  return null
}

export function trackPageView(path: string) {
  ga4PageView(path)
  const aiSource =
    typeof document !== 'undefined' && typeof window !== 'undefined'
      ? classifyAiReferral(document.referrer, window.location.search)
      : null
  posthog?.capture('$pageview', {
    $current_url: path,
    ...(aiSource ? { ai_referral_source: aiSource } : {}),
  })
  if (aiSource) {
    ga4Event('ai_referral', { ai_source: aiSource, page_path: path })
    posthog?.capture('ai_referral', { ai_source: aiSource, page_path: path })
  }
}


export function trackSignup(method: string) {
  ga4Event('sign_up', { method })
  posthog?.capture('signup', { method })
}

/**
 * Returning-user login (distinct from signup). Fires once per successful Clerk
 * sign-in on the login page. Gives us a login funnel signal that trackSignup
 * (first-time, post-onboarding) does not cover.
 */
export function trackLogin(method: string) {
  ga4Event('login', { method })
  posthog?.capture('login', { method })
}

export function trackCheckoutStarted(plan: string, billing: string) {
  ga4Event('begin_checkout', { items: [{ item_id: plan, item_name: plan }] })
  posthog?.capture('checkout_started', { plan, billing })
}

export function trackCheckoutCompleted(plan: string) {
  ga4Event('purchase', { items: [{ item_id: plan, item_name: plan }] })
  posthog?.capture('checkout_completed', { plan })
}

export function trackApiKeyRevoked() {
  ga4Event('api_key_revoked')
  posthog?.capture('api_key_revoked')
}

export function trackApiKeyCreated() {
  ga4Event('api_key_created')
  posthog?.capture('api_key_created')
}

export function trackApiKeyRotated() {
  ga4Event('api_key_rotated')
  posthog?.capture('api_key_rotated')
}

export function trackUpgradeClicked(from: string) {
  ga4Event('select_item', { items: [{ item_id: 'pro' }], from })
  posthog?.capture('upgrade_clicked', { from })
}

// ── Primate Vision / Product ──────────────────────────────────────────────────

export function trackWaitlistSubmit(success: boolean) {
  ga4Event('waitlist_submit', { success })
  posthog?.capture('waitlist_submit', { success })
}

export function trackInviteAccepted() {
  ga4Event('invite_accepted')
  posthog?.capture('invite_accepted')
}

export function trackOnboardingCompleted(useCase: string, plan: string) {
  ga4Event('onboarding_completed', { use_case: useCase, plan })
  posthog?.capture('onboarding_completed', { use_case: useCase, plan })
}

export function trackOnboardingStep(props: {
  step: number
  step_name: string
}) {
  ga4Event('onboarding_step_viewed', props)
  posthog?.capture('onboarding_step_viewed', props)
}

export function trackVideoUploaded(props: {
  file_size_bytes: number
  file_size_mb: number
  filename: string
  user_plan: string
  is_authenticated: boolean
}) {
  ga4Event('video_uploaded', props)
  posthog?.capture('video_uploaded', props)
}

export function trackVideoAnalysed(props: {
  prompt: string
  prompt_is_custom: boolean
  source: 'fresh' | 'run_prompt'
  user_plan: string
  is_authenticated: boolean
}) {
  ga4Event('video_analysed', props)
  posthog?.capture('video_analysed', props)
}

export function trackAnalysisResult(props: {
  answer: 'Yes' | 'No' | null
  query_type: string | null
  has_confidence: boolean
  user_plan: string
}) {
  ga4Event('analysis_result', props)
  posthog?.capture('analysis_result', props)
}

export function trackAnalysisCancelled(user_plan: string) {
  ga4Event('analysis_cancelled', { user_plan })
  posthog?.capture('analysis_cancelled', { user_plan })
}

export function trackDemoQuotaHit() {
  ga4Event('demo_quota_hit')
  posthog?.capture('demo_quota_hit')
}

export function trackVideoDeleted() {
  ga4Event('video_deleted')
  posthog?.capture('video_deleted')
}

export function trackVideoSessionCleared() {
  ga4Event('video_session_cleared')
  posthog?.capture('video_session_cleared')
}

// Single result pill deletion (DELETE /v1/user-videos/:id/session/:resultId)
export function trackResultPillDeleted(props: {
  pill_type: 'stream' | 'upload'
  user_plan: string
}) {
  ga4Event('result_pill_deleted', props)
  posthog?.capture('result_pill_deleted', props)
}

// Prompt parsing (POST /v1/parse — PRI-291)
// Fires once per resolved /v1/parse call. parse_mode reveals Haiku vs heuristic split.
export function trackQueryParsed(props: {
  parse_mode: 'haiku' | 'heuristic'
  prompt_length: number
  duration_ms: number
}) {
  ga4Event('query_parsed', props)
  posthog?.capture('query_parsed', props)
}

export function trackQueryParseFailed(props: {
  error_type: 'parse_failed' | 'network_error'
  prompt_length: number
  status?: number
}) {
  ga4Event('query_parse_failed', props)
  posthog?.capture('query_parse_failed', props)
}

// Stripe billing portal — existing-subscriber retention signal
export function trackBillingPortalOpened(props: { plan: string }) {
  ga4Event('billing_portal_opened', props)
  posthog?.capture('billing_portal_opened', props)
}

// Careers mailto click
export function trackCareersMailtoClicked(props: {
  source: 'careers_page' | 'waitlist_page'
  viewport: 'mobile' | 'desktop'
}) {
  ga4Event('careers_mailto_clicked', props)
  posthog?.capture('careers_mailto_clicked', props)
}

export function trackStreamHistoryViewed() {
  ga4Event('stream_history_viewed')
  posthog?.capture('stream_history_viewed')
}

export function trackProfileNameUpdated() {
  ga4Event('profile_name_updated')
  posthog?.capture('profile_name_updated')
}

export function trackDemoVideoSelected(props: {
  demo_video_id: string;
  demo_video_name: string;
  prompt: string;
  has_precomputed_result: boolean;
  user_plan: string;
  is_authenticated: boolean;
}) {
  ga4Event('demo_video_selected', props);
  posthog?.capture('demo_video_selected', props);
}

export function trackVideoLibraryTabSwitched(props: {
  tab: 'user' | 'demo' | 'stream';
  is_authenticated: boolean;
  user_plan: string;
}) {
  ga4Event('video_library_tab_switched', props);
  posthog?.capture('video_library_tab_switched', props);
}

export function trackUserVideoSaved(props: {
  filename: string;
  file_size_bytes: number;
  user_plan: string;
}) {
  ga4Event('user_video_saved', props);
  posthog?.capture('user_video_saved', props);
}

/**
 * A live-stream recording was persisted to S3 after the stream stopped. Distinct
 * from trackStreamStopped (which fires for every stream regardless of whether a
 * recording was saved). Lets us see how often streams are actually kept vs. just
 * watched. success=false means the presign/upload failed and we fell back to an
 * empty key (no playable recording saved).
 */
export function trackStreamRecordingSaved(props: {
  success: boolean;
  file_size_bytes: number;
  duration_s: number;
  user_plan: string;
}) {
  ga4Event('stream_recording_saved', props);
  posthog?.capture('stream_recording_saved', props);
}

/**
 * High-intent click on a /docs site CTA.
 * Legacy stub values: 'talk_to_team' | 'see_use_cases' (PRI-310 stub page).
 * PRI-438 docs site values: machine-readable spec links, API reference,
 * per-page markdown, and the signup link in content — all API-partner
 * interest signals.
 */
export function trackDocsCtaClicked(props: {
  cta:
    | 'talk_to_team'
    | 'see_use_cases'
    | 'api_reference'
    | 'openapi_spec'
    | 'llms_txt'
    | 'changelog_rss'
    | 'page_markdown'
    | 'signup';
}) {
  ga4Event('docs_cta_clicked', props);
  posthog?.capture('docs_cta_clicked', props);
}

export function trackStreamStarted(props: {
  prompt: string
  user_plan: string
  camera_label: string
  resolution: string
}) {
  ga4Event('stream_started', props)
  posthog?.capture('stream_started', props)
}

export function trackStreamQueued(props: {
  position: number
  user_plan: string
}) {
  ga4Event('stream_queued', props)
  posthog?.capture('stream_queued', props)
}

export function trackStreamLive(props: { user_plan: string }) {
  ga4Event('stream_live', props)
  posthog?.capture('stream_live', props)
}

export function trackStreamStopped(props: {
  duration_s: number
  stop_reason: string
  user_plan: string
  avg_fps: number
  avg_latency_ms: number
}) {
  ga4Event('stream_stopped', props)
  posthog?.capture('stream_stopped', props)
}

export function trackStreamQuotaWarning(props: {
  remaining_s: number
  user_plan: string
}) {
  ga4Event('stream_quota_warning', props)
  posthog?.capture('stream_quota_warning', props)
}

export function trackStreamQuotaExceeded(props: { user_plan: string }) {
  ga4Event('stream_quota_exceeded', props)
  posthog?.capture('stream_quota_exceeded', props)
}

export function trackStreamError(props: {
  error_code: string
  user_plan: string
}) {
  ga4Event('stream_error', props)
  posthog?.capture('stream_error', props)
}

export function trackStreamCameraDenied(props: { user_plan: string }) {
  ga4Event('stream_camera_denied', props)
  posthog?.capture('stream_camera_denied', props)
}

export function trackUseCaseCtaClicked(props: {
  cta: 'try_it_now' | 'talk_to_us'
  location: 'card' | 'bottom_section'
}) {
  ga4Event('use_case_cta_clicked', props)
  posthog?.capture('use_case_cta_clicked', props)
}

// ── Credits / billing (PRI-437 P8) ──────────────────────────────────────────

export function trackDiscountCodeRedeemed(code: string, type: string, plan_tier: string) {
  ga4Event("discount_code_redeemed", { code, type, plan_tier })
  posthog?.capture("discount_code_redeemed", { code, type, plan_tier })
}

export function trackUpgradeFromQuotaWall(trigger: "credits_exhausted" | "warning_banner" | "anon_trial_exhausted" | "anon_trial_low") {
  ga4Event("upgrade_from_quota_wall", { trigger })
  posthog?.capture("upgrade_from_quota_wall", { trigger })
}

export function trackCreditsPurchaseStarted(props: { amount_cents: number; method: 'saved_card' | 'checkout' }) {
  ga4Event('credits_purchase_started', props)
  posthog?.capture('credits_purchase_started', props)
}

export function trackCardSetupStarted() {
  ga4Event('card_setup_started', {})
  posthog?.capture('card_setup_started', {})
}

export function trackAutoRefillConfigured(props: { enabled: boolean; amount_cents: number | null }) {
  ga4Event('auto_refill_configured', props)
  posthog?.capture('auto_refill_configured', props)
}

export function trackDashboardUsagePeriodChanged(props: {
  period: 'month' | 'last_month' | 'all' | '7d' | '30d' | '90d'
}) {
  ga4Event('dashboard_usage_period_changed', props)
  posthog?.capture('dashboard_usage_period_changed', props)
}

// ── Narrative Streaming ──────────────────────────────────────────────────────────────────────

export function trackNarrativeGenerated(props: {
  entry_type: string
  session_type: 'stream' | 'upload'
  prompt_query_type: string
  user_plan: string
}): void {
  ga4Event('narrative_generated', props)
  posthog?.capture('narrative_generated', props)
}

export function trackNarrativeSessionSummary(props: {
  total_entries: number
  session_duration_s: number
  had_idle_warning: boolean
  session_type: 'stream' | 'upload'
  user_plan: string
}): void {
  ga4Event('narrative_session_summary', props)
  posthog?.capture('narrative_session_summary', props)
}

export function trackNarrativeFallback(props: {
  reason: 'llm_failure' | 'budget_cap' | 'grounding_fail'
  session_type: 'stream' | 'upload'
}): void {
  ga4Event('narrative_fallback', props)
  posthog?.capture('narrative_fallback', props)
}

export function trackNarrativeFeedback(props: {
  verdict: 'up' | 'down'
  entry_index: number
  session_type: 'stream' | 'upload'
}): void {
  ga4Event('narrative_feedback', props)
  posthog?.capture('narrative_feedback', props)
}

export function trackVideoRenamed(props: {
  video_id: string
  user_plan: string
}): void {
  ga4Event('video_renamed', props)
  posthog?.capture('video_renamed', props)
}

export function trackConversionDialogResult(props: {
  result: 'converted' | 'cancelled' | 'error'
  file_extension: string
  file_size_bytes: number
}): void {
  ga4Event('conversion_dialog_result', props)
  posthog?.capture('conversion_dialog_result', props)
}

export function trackBlogPostClicked(props: {
  slug: string
  title: string
  tag: string | undefined
  location: 'listing'
}): void {
  ga4Event('blog_post_clicked', props)
  posthog?.capture('blog_post_clicked', props)
}

// ── Session Download ──────────────────────────────────────────────────────────

export function trackDownloadStarted(props: { pill_type: 'stream' | 'upload'; user_plan: string }): void {
  ga4Event('download_started', props)
  posthog?.capture('download_started', props)
}

export function trackDownloadCompleted(props: { pill_type: 'stream' | 'upload'; user_plan: string; has_video: boolean }): void {
  ga4Event('download_completed', props)
  posthog?.capture('download_completed', props)
}

export function trackDownloadFailed(props: { pill_type: 'stream' | 'upload'; error: string; user_plan: string }): void {
  ga4Event('download_failed', props)
  posthog?.capture('download_failed', props)
}

// ── Mobile / Carousel ─────────────────────────────────────────────────────────

export function trackCarouselCtaClicked(props: {
  slide_index: number
  cta_label: string
  destination: string
}): void {
  ga4Event('carousel_cta_clicked', props)
  posthog?.capture('carousel_cta_clicked', props)
}

export function trackStreamPromptUpdated(props: {
  prompt: string
  user_plan: string
}): void {
  ga4Event('stream_prompt_updated', props)
  posthog?.capture('stream_prompt_updated', props)
}

// ── Onboarding Coach ────────────────────────────────────────────────────────

// Fired when the user dismisses the coaching walkthrough (session = "not now",
// persistent = "don't show again"). Critical for measuring onboarding completion
// rate vs. drop-off from the new mandatory-watch coaching flow (PRI-333).
export function trackCoachDismissed(props: {
  type: 'session' | 'persistent'
}): void {
  ga4Event('coach_dismissed', props)
  posthog?.capture('coach_dismissed', props)
}

// Fired when a user clicks "Compare to Gemini →" on the EmptyState demo.
export function trackCompareModalOpened(): void {
  ga4Event('compare_modal_opened')
  posthog?.capture('compare_modal_opened')
}

// Fired when a user clicks "Try for yourself" on the EmptyState demo.
// Key conversion signal: demo-watch → actual product use.
export function trackTryForYourselfClicked(): void {
  ga4Event('try_for_yourself_clicked')
  posthog?.capture('try_for_yourself_clicked')
}

// Fired when a user accepts the first-run / per-action Terms & Conditions gate
// (TermsModal). Critical consent-funnel signal: render → accept → first action.
export function trackTermsAccepted(): void {
  ga4Event('terms_accepted')
  posthog?.capture('terms_accepted')
}

// ── Account lifecycle ─────────────────────────────────────────────────────────

export function trackSignOut(): void {
  ga4Event('sign_out')
  posthog?.capture('sign_out')
}

export function trackDeleteAccountRequested(): void {
  ga4Event('delete_account_requested')
  posthog?.capture('delete_account_requested')
}

// ── V2 InputBar / CompiledQuery / ResultPill ─────────────────────────────────

export function trackBatchModeToggled(props: {
  enabled: boolean
  user_plan: string
}): void {
  ga4Event('batch_mode_toggled', props)
  posthog?.capture('batch_mode_toggled', props)
}

export function trackCompiledQueryEdited(props: {
  action: 'add_lane' | 'restore' | 'edit_toggle_open' | 'edit_toggle_close' | 'delete_lane'
  lane_count: number
  lane_type?: string
}): void {
  ga4Event('compiled_query_edited', props)
  posthog?.capture('compiled_query_edited', props)
}

export function trackResultCopied(props: {
  user_plan: string
}): void {
  ga4Event('result_copied', props)
  posthog?.capture('result_copied', props)
}

export function trackResultRerun(props: {
  user_plan: string
}): void {
  ga4Event('result_rerun', props)
  posthog?.capture('result_rerun', props)
}

// ── Onboarding coaching ─────────────────────────────────────────────────────

// Fired when the mandatory onboarding coaching demo completes its first full
// playthrough and sets the `primate-onboarding-v3-watched` localStorage flag.
// Distinct from `coach_dismissed` (which fires when the user skips). Key
// conversion signal: first-visit watch-gate → product unlock (PRI-333).
export function trackOnboardingVideoWatched(): void {
  ga4Event('onboarding_video_watched')
  posthog?.capture('onboarding_video_watched')
}

// ── Input / Source selection ─────────────────────────────────────────────────

// Fired when the user switches the video source in V2InputBar (webcam ↔ file).
// Only fires on an explicit user action — not on auto-selection from
// permission state. Key signal for understanding whether users prefer
// live-stream vs file-upload workflows.
export function trackSourceModeChanged(props: {
  mode: 'webcam' | 'file'
  user_plan: string
}): void {
  ga4Event('source_mode_changed', props)
  posthog?.capture('source_mode_changed', props)
}

// Billing notification preference toggle (PUT /v1/billing/notifications).
// Fires on each toggle — lets us see whether users are engaging with
// low-balance / refill-failed alerts or silencing them.
export function trackBillingNotificationsUpdated(props: {
  type: 'low_balance' | 'refill_failed'
  enabled: boolean
}): void {
  ga4Event('billing_notifications_updated', props)
  posthog?.capture('billing_notifications_updated', props)
}

// ── Device-code claim flow (PRI-456) ─────────────────────────────────────────

// Fired when a user lands on /claim/:code (or /claim?code=).
export function trackClaimViewed(props: { code: string }): void {
  ga4Event('claim_viewed', props)
  posthog?.capture('claim_viewed', props)
}

// Fired when the user clicks Approve and the claim POST returns 200.
export function trackClaimApproved(): void {
  ga4Event('claim_approved')
  posthog?.capture('claim_approved')
}

// Fired when the claim POST fails (network error or API error response).
export function trackClaimFailed(props: { reason: string }): void {
  ga4Event('claim_failed', props)
  posthog?.capture('claim_failed', props)
}

// ── OAuth consent ───────────────────────────────────────────────────────────

// Fired when the user Approves or Denies an OAuth 2.1 consent request
// on /oauth/consent (PRI-462). Critical funnel signal for MCP / agent auth.
export function trackOAuthConsentDecision(props: { decision: 'approve' | 'deny' }): void {
  ga4Event('oauth_consent_decision', props)
  posthog?.capture('oauth_consent_decision', props)
}

// ── API key interactions ──────────────────────────────────────────────────────

// Fired when the user clicks the Copy button on an API key row (PRI-466).
export function trackApiKeyCopied(): void {
  ga4Event('api_key_copied')
  posthog?.capture('api_key_copied')
}

// Fired when the user toggles the reveal/mask on an API key row (PRI-466).
export function trackApiKeyRevealed(props: { revealed: boolean }): void {
  ga4Event('api_key_revealed', props)
  posthog?.capture('api_key_revealed', props)
}

// ── Batch analysis (PRI-488) ────────────────────────────────────────────────

// Fired when the user clicks Run in BatchComposer and POST /v1/analyses/batch succeeds.
export function trackBatchAnalysisSubmitted(props: {
  prompt_count: number
  user_plan: string
}): void {
  ga4Event('batch_analysis_submitted', props)
  posthog?.capture('batch_analysis_submitted', props)
}

// ── Raw API tray ────────────────────────────────────────────────────────────

// Fired when a user opens the raw-API JSON tray on a result pill (PRI-494).
// PRI-552: optional metering_rule enrichment (null until terminal usage lands).
export function trackRawApiTrayOpened(props: { pill_id: string; is_stream: boolean; metering_rule?: 'per_second' | 'per_frame' | null }): void {
  ga4Event('raw_api_tray_opened', props)
  posthog?.capture('raw_api_tray_opened', props)
}

// ── Design Partner Agreement (PRI-586+) ────────────────────────────────────

// Fired when a design partner signs the agreement (free tier — no Stripe checkout).
export function trackDesignPartnerSigned(): void {
  ga4Event('design_partner_signed')
  posthog?.capture('design_partner_signed')
}

// Fired when a design partner signs and is redirected to Stripe Checkout.
export function trackDesignPartnerCheckoutStarted(): void {
  ga4Event('design_partner_checkout_started')
  posthog?.capture('design_partner_checkout_started')
}

// ── Error tracking ────────────────────────────────────────────────────────────

export function trackError(error: Error, context?: Record<string, unknown>): void {
  try {
    ga4Event('js_error', { message: error.message, ...context })
    posthog?.capture('js_error', { message: error.message, stack: error.stack, ...context })
  } catch {
    // Never throw from error tracking
  }
}
